import axios from 'axios';

//config
import configuration from 'configs/configuration';
//consts
import { ACCESS_TOKEN, REFRESH_TOKEN } from 'consts/auth';

const I18NEXTLNG = 'i18nextLng';

// Koliko prije isteka access tokena ga proaktivno osvjezimo.
const TOKEN_REFRESH_THRESHOLD_MS = 60000;

// Single-flight: dok jedan refresh traje, ostali pozivi cekaju isti rezultat (dashboard ispali
// 15-20 poziva odjednom pa bi inace svaki okinuo svoj refresh).
let refreshPromise = null;

const getTokenExpiryMs = token => {
  try {
    const payload = token.split('.')[1];
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

    return (claims.exp ?? 0) * 1000;
  } catch {
    return 0;
  }
};

const isTokenExpiringSoon = token => {
  const expiryMs = getTokenExpiryMs(token);

  return expiryMs > 0 && expiryMs - Date.now() < TOKEN_REFRESH_THRESHOLD_MS;
};

// Osvjezi access token preko postojeceg refresh granta. Vraca novi token; baca ako refresh ne uspije.
const refreshAccessToken = () => {
  if (!refreshPromise) {
    const formData = new FormData();
    formData.append('grant_type', REFRESH_TOKEN);
    formData.append(REFRESH_TOKEN, window.localStorage.getItem(REFRESH_TOKEN));

    refreshPromise = axios
      .post('/connect/token', formData, {
        'Content-Type': 'multipart/form-data',
      })
      .then(res => {
        window.localStorage.setItem(ACCESS_TOKEN, res.data.accessToken);

        return res.data.accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

axios.interceptors.request.use(
  async config => {
    config.baseURL = configuration.apiUrl;
    config.headers['Accept-Language'] = window.localStorage.getItem(I18NEXTLNG) ?? '';

    // /connect/* (token, logout) ne nose Bearer i ne smiju okidati proaktivni refresh (izbjegni petlju).
    if (config.url.includes('/connect/')) {
      return config;
    }

    let accessToken = window.localStorage.getItem(ACCESS_TOKEN);

    // Proaktivno osvjezi PRIJE slanja ako token istice - poziv ne ode s isteklim tokenom (nema 401 + retry).
    // Best-effort: ako refresh pukne, salje se sa starim tokenom pa reaktivni put ispod odradi svoje.
    if (
      accessToken &&
      window.localStorage.getItem(REFRESH_TOKEN) &&
      isTokenExpiringSoon(accessToken)
    ) {
      try {
        accessToken = await refreshAccessToken();
      } catch {
        accessToken = window.localStorage.getItem(ACCESS_TOKEN);
      }
    }

    if (accessToken) {
      config.headers['Authorization'] = 'Bearer ' + accessToken;
    }

    return config;
  },
  error => Promise.reject(error),
);

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response.status === 401 && originalRequest.url === '/connect/token') {
      window.location = '/login';

      return Promise.reject(error);
    }

    // Sigurnosna mreza: ako 401 ipak prodje (npr. razlika u satu), jednom osvjezi token i ponovi poziv.
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();

        originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;

        return axios(originalRequest);
      } catch (refreshError) {
        if (refreshError?.response?.status === 400) {
          window.localStorage.removeItem(ACCESS_TOKEN);
          window.localStorage.removeItem(REFRESH_TOKEN);

          window.location = '/login';
        }
      }
    }

    if (error.response.status === 403 && originalRequest.method === 'get') {
      window.location = '/403';
    }

    return Promise.reject(error);
  },
);

export default axios;
